import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import Button from './common/Button';
import { FORECAST_URL } from '../config/links';

const navLinks = [
  { href: '#revenue', label: 'Why ESF' },
  { href: '#product', label: 'Product' },
  { href: '#how-it-works', label: 'How it works' },
  { href: '#faq', label: 'FAQ' },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // The header sits over the hero's radial glow. Keeping it transparent until
  // the page moves lets the hero read as full-bleed, then the blur and border
  // fade in to keep the nav legible over content.
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close on Escape, and stop the page scrolling behind the open mobile menu.
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-esf-black/80 backdrop-blur-xl border-b border-white/[0.06]'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center" aria-label="Elite Sport Finance — home">
            <img
              src="/images/logo/logo-dark.svg"
              alt="Elite Sport Finance"
              width={416}
              height={80}
              className="h-8 md:h-9 w-auto"
            />
          </a>

          <nav className="hidden md:flex items-center gap-8" aria-label="Main">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-body-sm font-medium text-esf-gray-300 hover:text-esf-white transition-colors"
              >
                {link.label}
              </a>
            ))}
            <Button href={FORECAST_URL} variant="primary" size="md">
              Estimate your revenue
            </Button>
          </nav>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            className="md:hidden -mr-2 p-2 text-esf-gray-300 hover:text-esf-white transition-colors"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          className="md:hidden bg-esf-black/95 backdrop-blur-xl border-t border-white/[0.06]"
        >
          <nav className="px-4 py-6 space-y-1" aria-label="Mobile">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-body-lg font-medium text-esf-gray-300 hover:text-esf-white transition-colors"
              >
                {link.label}
              </a>
            ))}
            <Button
              href={FORECAST_URL}
              variant="primary"
              size="lg"
              className="mt-4 w-full"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Estimate your revenue
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
